import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as cadQuoteApi from '../../lib/api/cadQuote';

const formatWon = (value) => (value == null ? '-' : `${Math.round(Number(value) || 0).toLocaleString('ko-KR')}원`);
const formatNum = (value, digits = 2) => (value == null ? '-' : Number(value).toFixed(digits));

/**
 * 저장된 견적 1건을 읽기 전용으로 보여주는 다이얼로그 - "비슷한 과거 견적" 패널과
 * "저장된 견적" 목록 양쪽에서 행을 클릭하면 이 다이얼로그가 뜬다. 여기서는 값을
 * 고칠 수 없다(실제 견적가 입력/삭제는 각 목록에서 따로 한다).
 */
const CadQuoteSavedQuoteDialog = ({ open, qtUuid, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!open || !qtUuid) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setErrorMessage('');
    cadQuoteApi.getCadQuote(qtUuid)
      .then((res) => setDetail(res?.data || null))
      .catch(() => setErrorMessage('견적을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, [open, qtUuid]);

  // STEP 받기 - 브라우저가 흔히 쓰는 방식(blob → 임시 a 태그 클릭)으로 원본 파일을 내려받는다.
  const handleDownload = async () => {
    try {
      const res = await cadQuoteApi.downloadCadQuoteFile(qtUuid);
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = detail?.fileNm || 'cadquote.stp';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[cadQuote] STEP 다운로드 실패', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>저장된 견적 상세</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {!loading && !errorMessage && detail && (
          <>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', width: '35%' }}>품번</TableCell>
                  <TableCell>{detail.partNo || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>파일명</TableCell>
                  <TableCell>{detail.fileNm}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>재질</TableCell>
                  <TableCell>{detail.mainMtrlCd || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>부품/레이어 수</TableCell>
                  <TableCell>{detail.partCnt ?? '-'}/{detail.layerCnt ?? '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>전체 bbox</TableCell>
                  <TableCell>{detail.bbox?.x} × {detail.bbox?.y} × {detail.bbox?.z} mm</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>부피/표면적</TableCell>
                  <TableCell>{formatNum(detail.volumeCm3)} cm³ / {formatNum(detail.areaCm2)} cm²</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Divider sx={{ my: 1.5 }} />
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', width: '35%' }}>공급가</TableCell>
                  <TableCell>{formatWon(detail.supplyAmt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>부가세</TableCell>
                  <TableCell>{formatWon(detail.vatAmt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{formatWon(detail.totalAmt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>실제 견적가</TableCell>
                  <TableCell>{detail.finalAmt != null ? formatWon(detail.finalAmt) : '(미입력)'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>등록</TableCell>
                  <TableCell>{detail.regNm} / {detail.regDt}</TableCell>
                </TableRow>
                {detail.memo && (
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>비고</TableCell>
                    <TableCell>{detail.memo}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDownload} startIcon={<DownloadIcon />} disabled={!detail}>STEP 받기</Button>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CadQuoteSavedQuoteDialog;
